"use client";

import * as React from "react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

export const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  isLoading = false,
  className = '',
  disabled,
  ...props 
}: ButtonProps) => {
  const baseClasses = "inline-flex items-center justify-center font-semibold rounded-full shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation";
  
  const variantClasses = {
    primary: "bg-primary-500 text-white hover:bg-primary-900 active:bg-primary-900 hover:shadow-md",
    secondary: "bg-white text-primary-500 border border-primary-200 hover:bg-primary-100 active:bg-primary-100 hover:border-primary-300",
    outline: "border-2 border-primary-500 text-primary-500 hover:bg-primary-500 active:bg-primary-500 hover:text-white active:text-white",
    ghost: "text-primary-500 hover:bg-primary-100 active:bg-primary-100"
  };
  
  const sizeClasses = {
    sm: "px-4 py-2 text-sm min-h-[2.5rem]",
    md: "px-6 py-3 text-sm min-h-[3rem] md:text-base",
    lg: "px-8 py-4 text-base min-h-[3.5rem] md:text-lg"
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <svg className="animate-spin -ml-1 mr-3 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      {children}
    </button>
  );
};